import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * PostcardMania access gate (frontend counterpart of
 * canSendPostcardsProfile in functions/CRM/utils/callerRoles.js).
 *
 * CRM Admins / SuperAdmins always have access; everyone else needs
 * users/{uid}.postcardEnabled === true, which admins toggle from the
 * PostcardMania card on the Integrations page. Subscribes to the caller's
 * own user doc so the UI reacts in real time when an admin flips the switch.
 *
 * UI-only convenience — the callables and the campaign queue enforce the
 * same rule server-side.
 *
 * @returns {{ canSendPostcards: boolean, isCRMAdmin: boolean }}
 */
export default function usePostcardAccess() {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = Boolean(
    SuperAdmin || Roles?.includes('CRM Admin') || currentUser?.CRMRole === 'CRM Admin'
  );
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) {
      setEnabled(false);
      return undefined;
    }
    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (snap) => setEnabled(snap.exists() && snap.data()?.postcardEnabled === true),
      () => setEnabled(false)
    );
    return () => unsubscribe();
  }, [currentUser?.uid]);

  return { canSendPostcards: isCRMAdmin || enabled, isCRMAdmin };
}
